/* eslint-disable @typescript-eslint/no-explicit-any */
export default {
	name: 'booking',
	title: 'Booking',
	type: 'document',
	fields: [
		{
			name: 'bookingReference',
			title: 'Booking Reference',
			type: 'string',
			description:
				'Unique reference for this booking (e.g., Paystack reference)',
			validation: (Rule: any) => Rule.required(),
		},
		{
			name: 'status',
			title: 'Status',
			type: 'string',
			options: {
				list: [
					{ title: 'Pending', value: 'pending' },
					{ title: 'Confirmed', value: 'confirmed' },
					{ title: 'Completed', value: 'completed' },
					{ title: 'Cancelled', value: 'cancelled' },
				],
				layout: 'radio',
			},
			initialValue: 'confirmed',
			validation: (Rule: any) => Rule.required(),
		},
		{
			name: 'bookingType',
			title: 'Booking Type',
			type: 'string',
			options: {
				list: [
					{ title: 'Paid Service', value: 'paid' },
					{ title: 'Free/Consultation', value: 'free' },
					{ title: 'Enterprise/POA Request', value: 'enterprise' },
				],
			},
			validation: (Rule: any) => Rule.required(),
		},
		// Service Information
		{
			name: 'service',
			title: 'Service',
			type: 'reference',
			to: [{ type: 'service' }],
			description: 'Reference to the service booked',
		},
		{
			name: 'serviceTitle',
			title: 'Service Title',
			type: 'string',
			description: 'Title of the service (stored for reference)',
		},
		{
			name: 'servicePrice',
			title: 'Service Price',
			type: 'number',
			description: 'Price paid or quoted for the service',
		},
		{
			name: 'deliveryMode',
			title: 'Delivery Mode',
			type: 'string',
			options: {
				list: [
					{ title: 'Online', value: 'online' },
					{ title: 'In Person', value: 'inPerson' },
				],
			},
		},
		// Customer Information
		{
			name: 'fullName',
			title: 'Full Name',
			type: 'string',
			validation: (Rule: any) => Rule.required(),
		},
		{
			name: 'email',
			title: 'Email',
			type: 'string',
			validation: (Rule: any) => Rule.required().email(),
		},
		{
			name: 'phone',
			title: 'Phone',
			type: 'string',
			validation: (Rule: any) => Rule.required(),
		},
		{
			name: 'company',
			title: 'Company',
			type: 'string',
		},
		{
			name: 'location',
			title: 'Location',
			type: 'object',
			fields: [
				{
					name: 'city',
					title: 'City',
					type: 'string',
				},
				{
					name: 'state',
					title: 'State/Province',
					type: 'string',
				},
				{
					name: 'country',
					title: 'Country',
					type: 'string',
				},
			],
		},
		// Booking Schedule
		{
			name: 'selectedDate',
			title: 'Selected Date',
			type: 'datetime',
			description: 'Date and time of the booking',
		},
		{
			name: 'selectedTime',
			title: 'Selected Time',
			type: 'string',
			description: 'Time slot selected (e.g., "10:00")',
		},
		// Personal Information (for coaching services)
		{
			name: 'coachingReason',
			title: 'Coaching Reason',
			type: 'text',
			description: 'Why the customer needs coaching',
		},
		{
			name: 'challenges',
			title: 'Challenges',
			type: 'text',
			description: 'Areas of concern to address',
		},
		{
			name: 'currentRole',
			title: 'Current Role',
			type: 'string',
			description: "Customer's current role at work",
		},
		{
			name: 'publicSpeakingGoal',
			title: 'Public Speaking Goal',
			type: 'string',
			description: 'Public speaking goal for next 6 months',
		},
		// Enterprise/POA Fields
		{
			name: 'requestProposal',
			title: 'Request Proposal',
			type: 'boolean',
			description: 'Whether this is a proposal request',
			initialValue: false,
		},
		{
			name: 'industry',
			title: 'Industry',
			type: 'string',
		},
		{
			name: 'address',
			title: 'Address',
			type: 'text',
		},
		{
			name: 'participants',
			title: 'Number of Participants',
			type: 'string',
		},
		{
			name: 'customParticipants',
			title: 'Custom Participants',
			type: 'string',
		},
		{
			name: 'participantLevel',
			title: 'Participant Level',
			type: 'string',
		},
		{
			name: 'customLevel',
			title: 'Custom Level',
			type: 'string',
		},
		{
			name: 'communicationSituations',
			title: 'Communication Situations',
			type: 'array',
			of: [{ type: 'string' }],
		},
		{
			name: 'communicationGoals',
			title: 'Communication Goals',
			type: 'array',
			of: [{ type: 'string' }],
		},
		{
			name: 'otherGoal',
			title: 'Other Goal',
			type: 'text',
		},
		{
			name: 'trainingDate',
			title: 'Training Date',
			type: 'string',
			description: 'Requested training date',
		},
		{
			name: 'trainingFormat',
			title: 'Training Format',
			type: 'string',
		},
		{
			name: 'duration',
			title: 'Duration',
			type: 'string',
		},
		{
			name: 'customDuration',
			title: 'Custom Duration',
			type: 'string',
		},
		{
			name: 'customLocation',
			title: 'Custom Location',
			type: 'string',
		},
		{
			name: 'additionalInfo',
			title: 'Additional Information',
			type: 'text',
		},
		{
			name: 'budget',
			title: 'Budget',
			type: 'string',
		},
		{
			name: 'referralSource',
			title: 'Referral Source',
			type: 'string',
		},
		{
			name: 'customReferral',
			title: 'Custom Referral',
			type: 'string',
		},
		// Payment Information
		{
			name: 'paymentReference',
			title: 'Payment Reference',
			type: 'string',
			description: 'Paystack payment reference',
		},
		{
			name: 'paymentAmount',
			title: 'Payment Amount',
			type: 'number',
			description: 'Amount paid (in ZAR)',
		},
		{
			name: 'paymentStatus',
			title: 'Payment Status',
			type: 'string',
			options: {
				list: [
					{ title: 'Not Required', value: 'not_required' },
					{ title: 'Pending', value: 'pending' },
					{ title: 'Success', value: 'success' },
					{ title: 'Failed', value: 'failed' },
				],
			},
		},
		{
			name: 'paymentDate',
			title: 'Payment Date',
			type: 'datetime',
			description: 'When payment was made',
		},
		// Calendar Integration
		{
			name: 'calendarEventId',
			title: 'Calendar Event ID',
			type: 'string',
			description: 'Google Calendar event ID',
		},
		{
			name: 'calendarEventCreated',
			title: 'Calendar Event Created',
			type: 'boolean',
			description: 'Whether a calendar event was successfully created',
			initialValue: false,
		},
		{
			name: 'calendarEventError',
			title: 'Calendar Event Error',
			type: 'text',
			description: 'Error message if calendar creation failed',
		},
		// Metadata
		{
			name: 'notes',
			title: 'Admin Notes',
			type: 'text',
			description: 'Internal notes about this booking',
		},
	],
	preview: {
		select: {
			title: 'fullName',
			subtitle: 'serviceTitle',
			date: 'selectedDate',
			status: 'status',
		},
		prepare(selection: any) {
			const { title, subtitle, date, status } = selection;
			return {
				title: title,
				subtitle: `${subtitle || 'Service'} - ${status || 'pending'}`,
				description: date ? new Date(date).toLocaleDateString() : 'No date set',
			};
		},
	},
	orderings: [
		{
			title: 'Booking Date, New',
			name: 'bookingDateDesc',
			by: [{ field: '_createdAt', direction: 'desc' }],
		},
		{
			title: 'Booking Date, Old',
			name: 'bookingDateAsc',
			by: [{ field: '_createdAt', direction: 'asc' }],
		},
		{
			title: 'Selected Date, New',
			name: 'selectedDateDesc',
			by: [{ field: 'selectedDate', direction: 'desc' }],
		},
	],
};
